import type { DocMigration } from "../docMigration";

enum DocEntityPrefix {
  Annotation = "annotation",
  AnnotationTemplate = "annotationTemplate",
  AnnotationTemplateComponent = "annotationTemplateComponent",
  AuxOptionSet = "auxOptionSet",
  Block = "block",
  BuilderRef = "builderRef",
  Casework = "casework",
  Ceiling = "ceiling",
  Column = "column",
  DocRef = "docRef",
  Dormer = "dormer",
  DrawingRefs = "drawingRefs",
  EditMode = "editMode",
  Edge = "edge",
  EdgeModifier = "edgeModifier",
  ElectricalDevice = "electricalDevice",
  ElectricalLayout = "electricalLayout",
  ElectricalRule = "electricalRule",
  CatalogItem = "catalogItem",
  EdgeBehavior = "edgeBehavior",
  Elevation = "elevation",
  ElevationView = "elevationView",
  Facade = "facade",
  FacadeSpanStrata = "facadeSpanStrata",
  FacadeSweepStrata = "facadeSweepStrata",
  Floor = "floor",
  Foundation = "foundation",
  FoundationSystem = "foundationSystem",
  GableOpening = "gableOpening",
  GeneratedEntity = "generatedEntity",
  Group = "group",
  Level = "level",
  Measurement = "measurement",
  Modifier = "modifier",
  Opening = "opening",
  Option = "option",
  OptionEffect = "optionEffect",
  OptionRule = "optionRule",
  OptionSet = "optionSet",
  OptionSetOverrides = "optionSetOverrides",
  OptionSetRef = "optionSetRef",
  OptionTemplate = "optionTemplate",
  Palette = "palette",
  PlanView = "planView",
  Prop = "prop",
  Railing = "railing",
  RailingPost = "railingPost",
  ReferenceGeo = "referenceGeo",
  Roof = "roof",
  RoofSide = "roofSide",
  RoofSystem = "roofSystem",
  Room = "room",
  RoomLayout = "roomLayout",
  RoomLayoutInstance = "roomLayoutInstance",
  Settings = "settings",
  SettingsSet = "settingsSet",
  Shape = "shape",
  Sheet = "sheet",
  Site = "site",
  Skylight = "skylight",
  StairBlock = "stairBlock",
  StairSystem = "stairSystem",
  Underlay = "underlay",
  Vertex = "vertex",
  View3D = "view3D",
  PlanSetTemplate = "planSetTemplate",
  ContentTemplate = "contentTemplate",
  ContentTemplateInstance = "contentTemplateInstance",
  ContentReplicator = "contentReplicator",
  Viewport = "viewport",
}

const DocEntityPrefixToDocTableName = {
  [DocEntityPrefix.Annotation]: "annotation",
  [DocEntityPrefix.AuxOptionSet]: "auxOptionSets",
  [DocEntityPrefix.Block]: "blocks",
  [DocEntityPrefix.BuilderRef]: "builderRefs",
  [DocEntityPrefix.Casework]: "casework",
  [DocEntityPrefix.Ceiling]: "ceilings",
  [DocEntityPrefix.Column]: "columns",
  [DocEntityPrefix.DocRef]: "docRefs",
  [DocEntityPrefix.Dormer]: "dormers",
  [DocEntityPrefix.DrawingRefs]: "drawingRefs",
  [DocEntityPrefix.EdgeModifier]: "edgeModifiers",
  [DocEntityPrefix.ElectricalDevice]: "electricalDevices",
  [DocEntityPrefix.ElectricalLayout]: "electricalLayouts",
  [DocEntityPrefix.Elevation]: "elevations",
  [DocEntityPrefix.Facade]: "facades",
  [DocEntityPrefix.Floor]: "floors",
  [DocEntityPrefix.Foundation]: "foundations",
  [DocEntityPrefix.FoundationSystem]: "foundationSystems",
  [DocEntityPrefix.GableOpening]: "gableOpenings",
  [DocEntityPrefix.Group]: "groups",
  [DocEntityPrefix.Level]: "levels",
  [DocEntityPrefix.Measurement]: "measurements",
  [DocEntityPrefix.Modifier]: "modifiers",
  [DocEntityPrefix.Opening]: "openings",
  [DocEntityPrefix.OptionEffect]: "optionEffects",
  [DocEntityPrefix.OptionRule]: "optionRules",
  [DocEntityPrefix.OptionSet]: "optionSets",
  [DocEntityPrefix.OptionSetOverrides]: "optionSetOverrides",
  [DocEntityPrefix.OptionSetRef]: "optionSetRefs",
  [DocEntityPrefix.OptionTemplate]: "optionTemplates",
  [DocEntityPrefix.Palette]: "palettes",
  [DocEntityPrefix.PlanView]: "planViews",
  [DocEntityPrefix.Prop]: "props",
  [DocEntityPrefix.Railing]: "railings",
  [DocEntityPrefix.RailingPost]: "railingPosts",
  [DocEntityPrefix.ReferenceGeo]: "referenceGeom",
  [DocEntityPrefix.Roof]: "roofs",
  [DocEntityPrefix.RoofSystem]: "roofSystems",
  [DocEntityPrefix.Room]: "rooms",
  [DocEntityPrefix.RoomLayout]: "roomLayouts",
  [DocEntityPrefix.RoomLayoutInstance]: "roomLayoutInstances",
  [DocEntityPrefix.Settings]: "settings",
  [DocEntityPrefix.SettingsSet]: "settingsSets",
  [DocEntityPrefix.Sheet]: "sheets",
  [DocEntityPrefix.Site]: "sites",
  [DocEntityPrefix.Skylight]: "skylights",
  [DocEntityPrefix.StairBlock]: "stairBlocks",
  [DocEntityPrefix.StairSystem]: "stairSystems",
  [DocEntityPrefix.Underlay]: "underlays",
  [DocEntityPrefix.View3D]: "view3Ds",
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_307: DocMigration = {
  maxSchemaVersion: "0.0.307",
  description: "Remove orphaned or malformed entity groups",
  apply(json: any): any {
    for (const [hash, groupId] of Object.entries(json.entityGroups)) {
      let deleteEntityGroup = false;

      // Malformed or undefined hash
      if (!hash) {
        deleteEntityGroup = true;
      } else {
        // Referenced entity does not exist on the doc
        const [prefix, entityId] = hash ? hash.split(".") : [];
        const field = DocEntityPrefixToDocTableName[prefix as DocEntityPrefix];

        if (!field) {
          continue;
        }

        if (entityId && field in json && !json[field]?.[entityId]) {
          deleteEntityGroup = true;
        }
      }

      if (deleteEntityGroup) {
        delete json.entityGroups[hash];
        const groupEmpty = !Object.values(json.entityGroups).some((x) => x === groupId);

        if (groupEmpty) {
          delete json.groups[groupId as string];
        }
      }
    }

    return json;
  },
};

export default migration_0_0_307;
